import { GET_TRANSITIONAL_CARE } from "core/apollo/graphql";
import { SEARCH_ACTION_GENERATE_TRANSITIONAL_CARE_FORM } from "core/consts";
import { useIsDocsAndExtractActive } from "core/model/careseekers";
import { getErrorMessage } from "core/model/utils/errors";
import {
  Auction,
  TransitionalCare,
  TransitionalCareFormData,
} from "core/types";
import { useToast } from "dsl/atoms/ToastNotificationContext";
import { scrollToTop } from "dsl/hooks/useScrollTo";
import useSearchAction from "dsl/hooks/useSearchAction";
import { useCallback, useMemo } from "react";
import {
  SimpleFormRenderProps,
  composeValidation,
  convertEncryptedFieldIn,
  convertEncryptedFieldOut,
  validateModel,
} from "react-forms-state";
import { FormContainerState } from "react-forms-state/src/FormContainer";
import { useTranslations } from "translations";
import { ExitPreventionWrapper } from "../../utils";
import { TransitionalCareFormPresenter } from "./FormSections";
import { transitionalCareModelDefinition } from "./modelDefinition";
import {
  validatePatientConfirmation,
  validateTransitionalCareFormDates,
} from "./utils";

export const useGetTransitionalCareOnSubmit = ({
  auction,
}: {
  auction: Auction;
}) => {
  const translations = useTranslations();
  const [
    generateTransitionalCareForm,
    generateTransitionalCareFormProgress,
    resetProgress,
  ] = useSearchAction({
    actionType: SEARCH_ACTION_GENERATE_TRANSITIONAL_CARE_FORM,
  });
  const toast = useToast();
  const { isRedesignActive } = useIsDocsAndExtractActive();

  const onSubmit = useCallback(
    async (
      formData: TransitionalCareFormData,
      formState: FormContainerState,
    ) => {
      if (!formState.dirty) return;

      const updatedAuction = {
        ...auction,
        transitional_care: {
          form_data: convertEncryptedFieldOut(JSON.stringify(formData)),
        },
      } as Auction;
      await generateTransitionalCareForm({
        auction,
        context: {
          auction: updatedAuction,
        },
        onCompleted: (generated, client) => {
          if (!generated) return;

          client.writeQuery({
            data: {
              auction: {
                id: auction.id,
                seald_encryption_context: generated.seald_encryption_context,
                session_key_context: generated.session_key_context,
                transitional_care: generated.transitional_care,
              },
            },
            query: GET_TRANSITIONAL_CARE,
            variables: {
              auctionId: auction.id,
              patientId: auction.patient.id,
            },
          });
          if (!isRedesignActive) {
            toast({
              action: {
                label: translations.patientForms.transitionalCareForm.toTheTop,
                onClick: () => scrollToTop(),
              },
              color: "success",
              message:
                translations.patientForms.transitionalCareForm.pdfCreated,
            });
          }

          resetProgress();
        },
        onError: (err) => {
          console.error(`error saving rehab forms - ${getErrorMessage(err)}`);
          toast({
            color: "danger",
            message: translations.auctionRequest.tryAgain,
          });
        },
      });
    },
    [convertEncryptedFieldOut, generateTransitionalCareForm, auction],
  );

  return { onSubmit, onSubmitProgress: generateTransitionalCareFormProgress };
};

export const getTransitionalCareFormData = ({
  transitionalCare,
}: {
  transitionalCare: Readonly<TransitionalCare> | undefined;
}): TransitionalCareFormData | null => {
  if (!transitionalCare?.form_data) return null;

  const stringified = convertEncryptedFieldIn(
    transitionalCare.form_data as AnyObject,
  );

  if (stringified) {
    return JSON.parse(stringified) as TransitionalCareFormData;
  }

  return null;
};

const useGetFormInputValue = ({
  transitionalCare,
}: {
  transitionalCare: Readonly<TransitionalCare> | undefined;
}): TransitionalCareFormData | null => {
  return useMemo(
    () => getTransitionalCareFormData({ transitionalCare }),
    [transitionalCare?.form_data],
  );
};

export function TransitionalCareForm({
  auction,
  transitionalCare,
}: {
  auction: Auction;
  transitionalCare: Readonly<TransitionalCare> | undefined;
}) {
  const { onSubmit, onSubmitProgress } = useGetTransitionalCareOnSubmit({
    auction,
  });
  const formInputValue = useGetFormInputValue({ transitionalCare });

  return (
    <SimpleFormRenderProps
      asHtmlForm
      formInputValue={formInputValue}
      modelDefinition={transitionalCareModelDefinition}
      validate={composeValidation(
        validateModel(transitionalCareModelDefinition),
        validatePatientConfirmation,
        validateTransitionalCareFormDates,
      )}
      onSubmit={onSubmit}
    >
      {({ dirty, onChange: formOnChange, submit }) => (
        <ExitPreventionWrapper isDirty={dirty}>
          <TransitionalCareFormPresenter
            auction={auction}
            formOnChange={formOnChange}
            hasPreviousFormValue={!!transitionalCare}
            submit={submit}
            onSubmitProgress={onSubmitProgress}
          />
        </ExitPreventionWrapper>
      )}
    </SimpleFormRenderProps>
  );
}
